import { catalogPhoto, imageBytes } from './productPhotoCatalog.js';

const mm = value => value * 72 / 25.4;
export function magnetLabelPositions(count, skip = 0) {
  if (!Number.isInteger(skip) || skip < 0 || skip > 7) throw new Error('Selecciona un espacio inicial del 1 al 8.');
  if (!Number.isInteger(count) || count < 0) throw new Error('Cantidad de etiquetas no válida.');
  return Array.from({ length: count }, (_, index) => {
    const slot = index + skip;
    return { page: Math.floor(slot / 8), x: mm((slot % 2) * 105), top: mm(297 - Math.floor((slot % 8) / 2) * 74), width: mm(105), height: mm(74) };
  });
}

export function magnetLabelColor(product) {
  const hex = product.family?.magnet_label_color;
  if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) return [0, 0, 0];
  return [1, 3, 5].map(start => parseInt(hex.slice(start, start + 2), 16) / 255);
}

export async function createMagnetLabels(products, skip = 0, { loadImage = imageBytes, onProgress = () => {} } = {}) {
  const positions = magnetLabelPositions(products.length, skip);
  if (!products.length) throw new Error('No hay artículos para imprimir.');
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const safe = value => Array.from(String(value ?? '').replace(/\s+/g, ' ')).map(char => {
    try { font.encodeText(char); return char; } catch { return '?'; }
  }).join('');
  const wrap = (text, face, size, width) => {
    const lines = []; let line = '';
    for (const char of safe(text)) {
      if (line && face.widthOfTextAtSize(line + char, size) > width) { lines.push(line); line = ''; }
      line += char;
    }
    if (line) lines.push(line);
    return lines;
  };
  for (let index = 0; index < products.length; index++) {
    const product = products[index], box = positions[index];
    if (pdf.getPageCount() <= box.page) pdf.addPage([mm(210), mm(297)]);
    const page = pdf.getPages()[box.page];
    const padding = mm(5), width = box.width - padding * 2;
    // Keep all ink away from label edges; the final 1 mm of A4 stays blank.
    const photoHeight = mm(48), textHeight = mm(14);
    let size = 16, blocks;
    do {
      blocks = [[product.code, bold]]
        .map(([text, face]) => ({ lines: wrap(text, face, size, width), face }));
      if (blocks.reduce((sum, block) => sum + block.lines.length, 0) * size * 1.2 <= textHeight) break;
      size -= 0.5;
    } while (size > 5);
    let y = box.top - padding - photoHeight - mm(2) - size;
    const maxLines = Math.floor(textHeight / (size * 1.2));
    let used = 0;
    for (const block of blocks) for (let line of block.lines) {
      if (used >= maxLines) break;
      if (used === maxLines - 1 && blocks.reduce((n, b) => n + b.lines.length, 0) > maxLines) line = line.slice(0, -3) + '...';
      const textWidth = block.face.widthOfTextAtSize(line, size);
      page.drawText(line, { x: box.x + (box.width - textWidth) / 2, y, size, font: block.face, color: rgb(...magnetLabelColor(product)) });
      used++; y -= size * 1.2;
    }
    const source = catalogPhoto(product);
    if (source) {
      let photo;
      try { photo = await pdf.embedPng(await loadImage(source)); }
      catch (error) { throw new Error(`No se pudo cargar la foto de ${product.code}: ${error.message}`); }
      const fitted = photo.scaleToFit(width, photoHeight);
      page.drawImage(photo, { x: box.x + padding + (width - fitted.width) / 2, y: box.top - padding - photoHeight + (photoHeight - fitted.height) / 2, ...fitted });
    }
    onProgress(index + 1, products.length);
  }
  return pdf.save();
}
